import type { BillingPeriod, PurchasablePlan } from '../checkout/catalogue'
import { childPrice, plansFor } from '../checkout/catalogue'
import { dueTodayCents } from '../checkout/pricing'
import type { PromoCode } from '../promo/codes'
import { emit } from './events'

/**
 * What the visitor does, told to the page's Google Analytics and Meta pixel.
 *
 * The bundle carries no analytics code of its own, as with Sentry
 * (`report.ts`): the Webflow head loads gtag and the Meta pixel before this
 * script, each gated by the consent Termly reports, so a visitor who declined
 * is already covered at the vendor's own layer, and one whose blocker stops a
 * vendor is normal, so this quietly does nothing then. Tracking must never
 * break the widget that tracked.
 *
 * One catalogue of events, below, says what each is called at each vendor and
 * with which parameters, so a component names the interaction and knows
 * nothing of either. Google gets every event, its recommended ones by their
 * names and ours by ours; Meta gets the three it asks for before the shop's
 * own pixel takes over at checkout: ViewContent, AddToCart and Lead. Nothing
 * personal goes in a parameter: items are catalogue variants, values are
 * dollars, and an account event says only what kind of thing changed.
 *
 * Every event is also emitted on the bundle's bus as `analytics`, so the
 * page's own code can listen with `Codeverse.on` and tell anyone else.
 */

/** A product in Google's ecommerce shape: a catalogue variant, as one child's line. */
export interface Item {
  item_id: string
  item_name: string
  item_variant: BillingPeriod
  price: number
  quantity: 1
}

export type ItemList = 'pricing' | 'checkout'

export type AnalyticsEvent =
  /** A list of plans came into view: the pricing page's cards, or the checkout's. */
  | { kind: 'view_items'; list: ItemList; items: Item[] }
  /** A plan was chosen from a list: a pricing-page link, or a checkout card. */
  | { kind: 'select_item'; list: ItemList; item: Item }
  | { kind: 'select_billing'; billing: BillingPeriod }
  | { kind: 'add_sibling'; kids: number }
  /** Continue: the cart is being created, one item per child. */
  | { kind: 'add_to_cart'; items: Item[]; value: number }
  /** The nav cart was clicked with a draft in progress. */
  | { kind: 'view_cart'; items: Item[]; value: number }
  | { kind: 'select_promotion'; code: PromoCode; component: string }
  | { kind: 'promo_dismissed'; code: PromoCode }
  /** The page's sign-up form signed the visitor up for a promo. */
  | { kind: 'lead'; code: PromoCode }
  | { kind: 'login'; method: 'password' | 'reset_link' }
  | { kind: 'login_failed'; reason: 'credentials' | 'other' }
  | { kind: 'password_reset_requested' }
  | { kind: 'logout' }
  | {
      kind: 'subscription_change'
      action: 'pause' | 'resume' | 'cancel' | 'skip'
      reason?: string
    }
  | {
      kind: 'account_update'
      setting: 'name' | 'password' | 'games_visibility' | 'shipping' | 'payment'
    }
  | { kind: 'open_studio' }

declare module './events' {
  interface CodeverseEventMap {
    analytics: AnalyticsEvent
  }
}

const CURRENCY = 'USD'

const dollars = (cents: number) => cents / 100

/** One child's line, in Google's shape. The pricing page lists the first child's. */
export function itemFor(plan: PurchasablePlan, billing: BillingPeriod, index = 0): Item {
  const { variantId, priceCents } = childPrice(plan, billing, index)
  return {
    item_id: variantId,
    item_name: index === 0 ? plan.name : `${plan.name} (sibling)`,
    item_variant: billing,
    price: dollars(priceCents),
    quantity: 1,
  }
}

/** The order as the checkout builds it: one item per child, siblings as the sibling product. */
export function cartFor(plan: PurchasablePlan, billing: BillingPeriod, kids: number) {
  return {
    items: Array.from({ length: kids }, (_, index) => itemFor(plan, billing, index)),
    value: dollars(dueTodayCents(plan, billing, kids)),
  }
}

/** Every plan a visitor with `promo` is sold, at `billing`, as a list's items. */
export function listFor(promo: PromoCode | null, billing: BillingPeriod): Item[] {
  return plansFor(promo).map((plan) => itemFor(plan, billing))
}

/** Google's arguments for an event: its name there, and its parameters. */
function forGoogle(event: AnalyticsEvent): [string, Record<string, unknown>] {
  switch (event.kind) {
    case 'view_items':
      return ['view_item_list', { item_list_id: event.list, items: event.items }]
    case 'select_item':
      return ['select_item', { item_list_id: event.list, items: [event.item] }]
    case 'select_billing':
      return ['select_billing', { billing: event.billing }]
    case 'add_sibling':
      return ['add_sibling', { kids: event.kids }]
    case 'add_to_cart':
    case 'view_cart':
      return [event.kind, { currency: CURRENCY, value: event.value, items: event.items }]
    case 'select_promotion':
      return [
        'select_promotion',
        { promotion_id: event.code, promotion_name: event.code, creative_slot: event.component },
      ]
    case 'promo_dismissed':
      return ['promo_dismissed', { promotion_id: event.code }]
    case 'lead':
      return ['generate_lead', { lead_source: event.code }]
    case 'login':
      return ['login', { method: event.method }]
    case 'login_failed':
      return ['login_failed', { reason: event.reason }]
    case 'subscription_change':
      return ['subscription_change', { action: event.action, reason: event.reason }]
    case 'account_update':
      return ['account_update', { setting: event.setting }]
    default:
      return [event.kind, {}]
  }
}

/** Meta's content parameters for a list of items. */
function contents(items: Item[]) {
  return {
    content_type: 'product',
    content_ids: items.map((item) => item.item_id),
    contents: items.map((item) => ({ id: item.item_id, quantity: 1, item_price: item.price })),
  }
}

/** Meta's arguments for an event, or none: its standard name there, and its parameters. */
function forMeta(event: AnalyticsEvent): [string, Record<string, unknown>] | undefined {
  switch (event.kind) {
    case 'view_items':
      return ['ViewContent', contents(event.items)]
    case 'add_to_cart':
      return [
        'AddToCart',
        {
          ...contents(event.items),
          currency: CURRENCY,
          value: event.value,
          num_items: event.items.length,
        },
      ]
    case 'lead':
      return ['Lead', { content_name: event.code }]
    default:
      return undefined
  }
}

/** Tell both vendors, and the bus. Never throws: a vendor's failure is its own. */
export function track(event: AnalyticsEvent): void {
  try {
    const [name, params] = forGoogle(event)
    window.gtag?.('event', name, params)
  } catch {
    // Google's problem, not the widget's.
  }
  try {
    const meta = forMeta(event)
    if (meta) window.fbq?.('track', ...meta)
  } catch {
    // Meta's problem, not the widget's.
  }
  emit('analytics', event)
}

/** The lists already told of, so a page with several embeds of one list says it once. */
const seen = new Set<string>()

/** Track an event once per page, by `key`. */
export function trackOnce(key: string, event: AnalyticsEvent): void {
  if (seen.has(key)) return
  seen.add(key)
  track(event)
}

/** Forget what was told of on this page. Tests only. */
export function resetAnalytics(): void {
  seen.clear()
}
